/**
 * Payment method gateway type constants
 * Used to categorize payment methods in checkout preview
 */

export const BNPL_GATEWAY_TYPES = [
  'sympl',
  'symplv2',
  'sympl_v2',
  'tamara',
  'tabby',
  'postpay',
  'forsa',
  'forsa_v2',
  'souhoola',
  'souhoolav2',
  'souhoolav3',
  'premium',
  'premium6',
  'aman',
  'aman_v2',
  'aman_v3',
  'amanv3',
  'contact',
  'tru',
  'halan',
  'klivvr',
  'mid_takseet',
  'maal',
  'seven',
  'valu',
] as const;

export const X_PAY_GATEWAY_TYPES = [
  'apple-pay',
  'google-pay',
  'paypal',
  'instapay',
] as const;

export type BnplGatewayType = typeof BNPL_GATEWAY_TYPES[number];
export type XPayGatewayType = typeof X_PAY_GATEWAY_TYPES[number];

/**
 * Normalized BNPL gateway names in uppercase format
 * Used for checking if a normalized gateway is a BNPL method
 */
export const BNPL_NORMALIZED_GATEWAYS = [
  'SYMPL',
  'SYMPL_V2',
  'TAMARA',
  'TABBY',
  'POSTPAY',
  'FORSA',
  'FORSA_V2',
  'SOUHOOLAV2',
  'SOUHOOLAV3',
  'PREMIUM',
  'PREMIUM6',
  'AMAN',
  'AMAN_V2',
  'AMANV3',
  'CONTACT',
  'TRU',
  'HALAN',
  'KLIVVR',
  'MID_TAKSEET',
  'MAAL',
  'SEVEN',
  'VALU',
] as const;

/**
 * Normalized X Pay gateway names
 * Used for checking if a normalized gateway is an X Pay method
 */
export const X_PAY_NORMALIZED_GATEWAYS = [
  'PAYPAL',
  'INSTAPAY',
  'apple-pay',
  'google-pay',
] as const;

/**
 * Normalizes gateway type to uppercase gateway identifier
 * Maps gateway_type (e.g., 'card', 'wallet') to normalized gateway name (e.g., 'MIGS', 'UIG')
 * Based on the old dashboard's PaymentTabContent.js gateway mappings
 * @param gatewayType - The gateway type from integration
 * @returns Normalized uppercase gateway identifier
 */
export const normalizeGatewayName = (gatewayType: string): string => {
  const gatewayTypeLower = gatewayType.toLowerCase();
  
  // Map gateway types to their normalized names (based on old dashboard)
  const gatewayMapping: Record<string, string> = {
    // Regular payment methods
    'card': 'MIGS',
    'wallet': 'UIG',
    'e-wallet': 'UIG',
    'stcpay': 'STCPAY',
    'kiosk': 'CAGG',
    'crypto': 'CRYPTO',
    'digital-wallet': 'DIGITAL_WALLET',
    'vpc': 'VPC',
    'card-installment': 'INSTALLMENT',
    'pay_later': 'PAY_LATER',
    'knet': 'KNET',
    'knet-apple-pay': 'Knet-apple-pay',
    'easypaisa': 'EASYPAISA',
    'easypaisadirect': 'EASYPAISADIRECT',
    'jazzcash': 'JAZZCASH',
    
    // BNPL methods
    'sympl': 'SYMPL',
    'symplv2': 'SYMPL_V2',
    'sympl_v2': 'SYMPL_V2',
    'tamara': 'TAMARA',
    'tabby': 'TABBY',
    'postpay': 'POSTPAY',
    'forsa': 'FORSA',
    'forsa_v2': 'FORSA_V2',
    'souhoola': 'SOUHOOLAV2',
    'souhoolav2': 'SOUHOOLAV2',
    'souhoolav3': 'SOUHOOLAV3',
    'premium': 'PREMIUM',
    'premium6': 'PREMIUM6',
    'aman': 'AMAN',
    'aman_v2': 'AMAN_V2',
    'aman_v3': 'AMANV3',
    'amanv3': 'AMANV3',
    'contact': 'CONTACT',
    'tru': 'TRU',
    'halan': 'HALAN',
    'klivvr': 'KLIVVR',
    'mid-takseet': 'MID_TAKSEET',
    'mid_takseet': 'MID_TAKSEET',
    'maal': 'MAAL',
    'seven': 'SEVEN',
    'valu': 'VALU',
    
    // X Pay methods
    'instapay': 'INSTAPAY',
    'apple-pay': 'apple-pay',
    'google-pay': 'google-pay',
    'paypal': 'PAYPAL',
  };

  // Check if we have a specific mapping
  if (gatewayMapping[gatewayTypeLower]) {
    return gatewayMapping[gatewayTypeLower];
  }

  // For unmapped methods, convert to uppercase with underscores
  // Handle special case for knet-apple-pay (keep as-is)
  if (gatewayTypeLower === 'knet-apple-pay') {
    return 'Knet-apple-pay';
  }
  
  return gatewayType.toUpperCase().replace(/[-]/g, '_');
};

/**
 * Formats a gateway type string to a display name
 * @param gatewayType - The gateway type (e.g., 'card', 'wallet', 'sympl')
 * @returns Formatted display name
 */
export const formatGatewayTypeToName = (gatewayType: string): string => {
  // Handle special cases
  if (gatewayType.toLowerCase() === 'pay_later') {
    return 'Pay Later';
  }

  // Convert kebab-case and snake_case to Title Case
  return gatewayType
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
